import type { Ref } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  internalBounds,
  CARD_PADDING,
  LINE_HEIGHT,
  NAME_FONT_SIZE,
  TEXT_FONT_SIZE,
  type C4Layout,
  type C4LayoutNode,
} from '../lib/c4Layout';

const INTERNAL = { fill: '#85BBF0', stroke: '#5D82A8', text: '#0B2E4F' };
const EXTERNAL = { fill: '#999999', stroke: '#6B6B6B', text: '#FFFFFF' };
const ARROW_MARKER_ID = 'c4-component-arrow';

export interface C4ComponentDiagramProps {
  layout: C4Layout;
  title: string;
  subtitle?: string;
  deployment?: string;
  svgRef?: Ref<SVGSVGElement>;
}

function Node({ node }: { node: C4LayoutNode }) {
  const palette = node.type === 'internal' ? INTERNAL : EXTERNAL;
  const centerX = node.x + node.width / 2;
  let textY = node.y + CARD_PADDING + NAME_FONT_SIZE;

  const nameY = textY;
  textY += LINE_HEIGHT;
  const subtitleY = textY;
  if (node.subtitle) {
    textY += LINE_HEIGHT;
  }

  return (
    <g data-testid={`c4-diagram-node-${node.id}`}>
      <rect
        x={node.x}
        y={node.y}
        width={node.width}
        height={node.height}
        rx={4}
        fill={palette.fill}
        stroke={palette.stroke}
        strokeWidth={2}
      />
      <text
        x={centerX}
        y={nameY}
        textAnchor="middle"
        fontSize={NAME_FONT_SIZE}
        fontWeight="bold"
        fill={palette.text}
      >
        {node.title}
      </text>
      {node.subtitle && (
        <text
          x={centerX}
          y={subtitleY}
          textAnchor="middle"
          fontSize={TEXT_FONT_SIZE}
          fill={palette.text}
          opacity={0.9}
        >
          {node.subtitle}
        </text>
      )}
      {node.descriptionLines.map((line, index) => (
        <text
          key={`${index}-${line}`}
          x={centerX}
          y={textY + index * LINE_HEIGHT}
          textAnchor="middle"
          fontSize={TEXT_FONT_SIZE}
          fill={palette.text}
          opacity={0.85}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

export function C4ComponentDiagram({
  layout,
  title,
  subtitle,
  deployment,
  svgRef,
}: C4ComponentDiagramProps) {
  const theme = useTheme();
  const bounds = internalBounds(layout);

  const edgeColor = theme.palette.textSubtle;
  const boundaryColor = theme.palette.divider;

  return (
    <svg
      ref={svgRef}
      data-testid="c4-component-diagram"
      role="img"
      aria-label={`Componentes internos de ${title}`}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      width="100%"
      height={layout.height}
      style={{ maxWidth: layout.width, height: 'auto' }}
      fontFamily={theme.typography.fontFamily}
    >
      <defs>
        <marker
          id={ARROW_MARKER_ID}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={edgeColor} />
        </marker>
      </defs>

      <rect
        width={layout.width}
        height={layout.height}
        fill={theme.palette.background.paper}
      />

      {bounds && (
        <>
          <rect
            data-testid="c4-diagram-boundary"
            x={bounds.x}
            y={bounds.y}
            width={bounds.width}
            height={bounds.height}
            rx={6}
            fill="none"
            stroke={boundaryColor}
            strokeWidth={2}
            strokeDasharray="8 6"
          />
          <text
            x={bounds.x}
            y={deployment ? bounds.y - 22 : bounds.y - 8}
            fontSize={TEXT_FONT_SIZE + 1}
            fill={theme.palette.text.secondary}
          >
            {subtitle ? `${title} ${subtitle}` : title}
          </text>
          {deployment && (
            <text
              data-testid="c4-diagram-deployment"
              x={bounds.x}
              y={bounds.y - 8}
              fontSize={TEXT_FONT_SIZE}
              fill={theme.palette.text.secondary}
              opacity={0.9}
            >
              {deployment}
            </text>
          )}
        </>
      )}

      {layout.edges.map(edge => (
        <path
          key={`${edge.from}->${edge.to}`}
          data-testid={`c4-diagram-edge-${edge.from}-${edge.to}`}
          d={edge.path}
          fill="none"
          stroke={edgeColor}
          strokeWidth={1.5}
          markerEnd={`url(#${ARROW_MARKER_ID})`}
        />
      ))}

      {layout.nodes.map(node => (
        <Node key={node.id} node={node} />
      ))}
    </svg>
  );
}
